import { useEffect, useState } from 'react';

import {
  Divider,
  type RowSelectionState,
  Text,
  type TypeSelectOption,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';

import { PageTitle } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import {
  ReceiveReturnDisposalClaimModal,
  ReceiveReturnDisposalDetailModal,
  ReceiveReturnDisposalRtsDirectModal,
  ReceiveReturnDisposalRtsParcelModal,
  ReceiveReturnDisposalScrapModal,
  ReceiveReturnDisposalSearchFilter,
  ReceiveReturnDisposalTable,
} from '~/components/receive';

import { useDownloadReady, useModal, useStableCallback, useToggle } from '~/hooks/common';
import {
  receiveKeys,
  useGetReturnDisposalFilterAllQuery,
  useGetReturnDisposalListDownloadMutation,
  useGetReturnDisposalListQuery,
} from '~/hooks/receive';
import {
  formatDate,
  getDateByOffset,
  isRtsPendingInputMixedSelection,
  isRtsPendingInputOnlySelection,
  isRtsPlannedAtMismatchSelection,
  isValidDateRange,
  renderFilterErrorMessage,
} from '~/utils';

import {
  BREAD_CRUMB,
  INITIAL_RECEIVE_PAGE_CURSOR,
  INITIAL_RECEIVE_PAGE_SIZE,
  MAX_RECEIVE_DATE_RANGE,
  OPTION_ALL,
  PATH,
  RETURN_DISPOSAL_REQUEST_MESSAGE,
  TOAST_MESSAGE,
  type TypeReturnDisposalRequestAction,
} from '~/constants';

import type {
  TypeReturnDisposalFilter,
  TypeReturnDisposalFilterStatus,
  TypeReturnDisposalInfo,
  TypeReturnDisposalListParams,
} from '~/types/receive.types';

const DEFAULT_RETURN_DISPOSAL_FILTER: TypeReturnDisposalFilter = {
  keywordType: null,
  keyword: '',
  center: OPTION_ALL,
  warehouse: OPTION_ALL,
  dateType: null,
  dateRange: { start: getDateByOffset(-6, 'day'), end: getDateByOffset(0, 'day') },
  releaseCode: OPTION_ALL,
  processMethod: OPTION_ALL,
  processStatus: OPTION_ALL,
  pagination: { currentPage: INITIAL_RECEIVE_PAGE_CURSOR, totalCount: 0, pageSize: INITIAL_RECEIVE_PAGE_SIZE },
};

/**
 * [입고관리 > 회송/폐기 관리] 페이지 컴포넌트
 *
 * @note 필터 옵션·목록을 BE 조회 API로 연동한다. 엑셀·상세 모달·처리신청은 후속(TODO 지점).
 */
const ReceiveReturnDisposalPage = () => {
  const queryClient = useQueryClient();

  const { data: filterOptions, isError: isFilterOptionsError } = useGetReturnDisposalFilterAllQuery({ silent: true });

  const [searchFilterValues, setSearchFilterValues] =
    useState<TypeReturnDisposalFilter>(DEFAULT_RETURN_DISPOSAL_FILTER);
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<TypeReturnDisposalFilterStatus>({
    dateRange: 'default',
  });

  const [listQueryParams, setListQueryParams] = useState<TypeReturnDisposalListParams | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const listQuery = useGetReturnDisposalListQuery({
    params: listQueryParams ?? ({} as TypeReturnDisposalListParams),
    enabled: !!listQueryParams,
  });

  useEffect(() => {
    setRowSelection({});
  }, [listQuery.data]);

  const { isDownloadReady, searchedParam, markDownloadReady, checkFilterChange } = useDownloadReady<
    TypeReturnDisposalFilter,
    TypeReturnDisposalListParams
  >({ initialFilter: DEFAULT_RETURN_DISPOSAL_FILTER });
  const downloadMutation = useGetReturnDisposalListDownloadMutation();

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const [selectedInspectionId, setSelectedInspectionId] = useState<number | null>(null);
  const detailModal = useModal();

  const [selectedInspectionIds, setSelectedInspectionIds] = useState<number[]>([]);
  /** 회수 신청 진입 시 선택 전건이 보류해제-회송(컬리 지정 회수예정일 고정) 건인지 */
  const [isRtsPendingInputSelection, setIsRtsPendingInputSelection] = useState(false);
  const rtsDirectModal = useModal();
  const rtsParcelModal = useModal();
  const scrapModal = useModal();
  const claimModal = useModal();

  const [prevFilterOptions, setPrevFilterOptions] = useState<typeof filterOptions | null>(null);
  if (filterOptions !== prevFilterOptions) {
    setPrevFilterOptions(filterOptions);
    setSearchFilterValues((prev) => ({
      ...prev,
      keywordType: filterOptions?.keywordType[0] ?? null,
      dateType: filterOptions?.dateType[0] ?? null,
    }));
  }

  useEffect(() => {
    if (isFilterOptionsError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [isFilterOptionsError]);

  const handleFilterChange = useStableCallback(() => checkFilterChange(searchFilterValues));

  useEffect(() => {
    handleFilterChange();
  }, [searchFilterValues, handleFilterChange]);

  const syncDownloadReady = useStableCallback(() => {
    if (listQuery.data && listQueryParams && !listQuery.isPlaceholderData) {
      markDownloadReady({
        filterValue: searchFilterValues,
        hasData: listQuery.data.totalCount > 0,
        searchedParam: listQueryParams,
      });
    }
  });

  useEffect(() => {
    syncDownloadReady();
  }, [listQuery.data, listQueryParams, listQuery.isPlaceholderData, syncDownloadReady]);

  const handleSearch = (filter: TypeReturnDisposalFilter = searchFilterValues) => {
    if (!filter.dateRange?.start || !filter.dateRange?.end) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, dateRange: 'error' }));

      dialogService.addDialog({ children: '시작일과 종료일을 모두 입력해주세요.' });
      return;
    }

    if (!isValidDateRange(filter.dateRange, MAX_RECEIVE_DATE_RANGE)) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, dateRange: 'error' }));

      dialogService.addDialog({ children: `최대 ${MAX_RECEIVE_DATE_RANGE}일 기간으로 입력 후 검색해주세요.` });
      return;
    }

    setSearchFilterValuesStatus((prev) => ({ ...prev, dateRange: 'default' }));

    const { keywordType, keyword, center, warehouse, dateType, dateRange, releaseCode, processMethod, processStatus } =
      filter;

    const params: TypeReturnDisposalListParams = {
      ...(keyword && keywordType && { codeType: keywordType.value }),
      ...(keyword && { code: keyword }),
      dateType: dateType?.value ?? '',
      fromDate: formatDate(dateRange?.start, 'YYYY-MM-DD') ?? '',
      toDate: formatDate(dateRange?.end, 'YYYY-MM-DD') ?? '',
      ...(center?.value && { cluster: center.value }),
      ...(warehouse?.value && { warehouse: warehouse.value }),
      ...(releaseCode?.value && { releaseCode: releaseCode.value }),
      ...(processMethod?.value && { processMethod: processMethod.value }),
      ...(processStatus?.value && { processStatus: processStatus.value }),
      limit: filter.pagination.pageSize,
      offset: filter.pagination.currentPage * filter.pagination.pageSize,
    };

    queryClient.invalidateQueries({ queryKey: receiveKeys.returnDisposalIssues(params) });

    setSearchFilterValues(filter);
    setListQueryParams(params);
    setRowSelection({});
  };

  // 처리신청(회수직배·택배·폐기·클레임) 성공 시 목록과 GNB 미처리 건수를 함께 갱신한다.
  const handleProcessSuccess = (closeModal: () => void) => {
    closeModal();
    handleSearch();
    queryClient.invalidateQueries({ queryKey: receiveKeys.returnDisposalUnprocessedCount() });
  };

  const handleResetFilter = () => {
    setSearchFilterValues({
      ...DEFAULT_RETURN_DISPOSAL_FILTER,
      keywordType: filterOptions?.keywordType[0] ?? null,
      dateType: filterOptions?.dateType[0] ?? null,
    });
    setSearchFilterValuesStatus({ dateRange: 'default' });
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_RECEIVE_PAGE_CURSOR, pageSize: value.value },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: value },
    });
  };

  const handleClickViewDetail = (data: TypeReturnDisposalInfo) => {
    setSelectedInspectionId(data.inspectionId);
    detailModal.handleOpenModal();
  };

  const handleClickExcelDownload = () => {
    if (!searchedParam) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { limit, offset, ...restParams } = searchedParam;

    downloadMutation.mutate(restParams, {
      onSuccess: () => toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS }),
    });
  };

  const handleClickProcessRequest = (
    action: TypeReturnDisposalRequestAction,
    selectedRows: TypeReturnDisposalInfo[]
  ) => {
    if (selectedRows.length === 0) return;

    if (action === 'RTS_DIRECT' || action === 'RTS_PARCEL') {
      // 회수 신청(직배·택배)은 입고요청번호 + 이슈발생센터가 모두 동일한 건만 일괄 신청 가능
      const groupKeys = new Set(selectedRows.map((row) => `${row.receiveRequestCode} ${row.issueCenterName}`));
      if (groupKeys.size > 1) {
        dialogService.addDialog({
          title: RETURN_DISPOSAL_REQUEST_MESSAGE.CENTER_MISMATCH_TITLE,
          children: RETURN_DISPOSAL_REQUEST_MESSAGE.CENTER_MISMATCH_BODY,
        });
        return;
      }

      // 보류해제-회송 건은 컬리 지정 회수예정일이 강제되어 자유 선택 카드와 한 신청으로 묶을 수 없다
      if (isRtsPendingInputMixedSelection(selectedRows)) {
        dialogService.addDialog({
          title: RETURN_DISPOSAL_REQUEST_MESSAGE.RTS_PENDING_INPUT_MIXED_TITLE,
          children: RETURN_DISPOSAL_REQUEST_MESSAGE.RTS_PENDING_INPUT_MIXED_BODY,
        });
        return;
      }

      // 보류해제-회송 건끼리도 컬리 지정 회수예정일이 같은 경우만 한 신청으로 묶을 수 있다
      if (isRtsPlannedAtMismatchSelection(selectedRows)) {
        dialogService.addDialog({
          title: RETURN_DISPOSAL_REQUEST_MESSAGE.RTS_PLANNED_AT_MISMATCH_TITLE,
          children: RETURN_DISPOSAL_REQUEST_MESSAGE.RTS_PLANNED_AT_MISMATCH_BODY,
        });
        return;
      }

      setIsRtsPendingInputSelection(isRtsPendingInputOnlySelection(selectedRows));
      setSelectedInspectionIds(selectedRows.map((row) => row.inspectionId));
      if (action === 'RTS_DIRECT') {
        rtsDirectModal.handleOpenModal();
      } else {
        rtsParcelModal.handleOpenModal();
      }
      return;
    }

    // 폐기는 이슈발생센터 제한이 없어 센터 검증 없이 바로 신청 모달 진입
    if (action === 'SCRAP') {
      setSelectedInspectionIds(selectedRows.map((row) => row.inspectionId));
      scrapModal.handleOpenModal();
      return;
    }

    // 클레임은 센터 제한이 없어 검증 없이 바로 신청 모달 진입(D+3 초과는 버튼 비활성으로 차단).
    if (action === 'CLAIM') {
      setSelectedInspectionIds(selectedRows.map((row) => row.inspectionId));
      claimModal.handleOpenModal();
    }
  };

  return (
    <PageWrapper>
      <PageTitle
        title="회송/폐기 관리"
        breadcrumbs={BREAD_CRUMB[PATH.RECEIVE_RETURN_DISPOSAL]}
        description={
          <Text size="medium" color="tertiary">
            입고 이슈 상품의 처리 방법을 이슈 카드별로 선택하고 관리하세요.
          </Text>
        }
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <div>
          <ReceiveReturnDisposalSearchFilter
            options={filterOptions}
            values={searchFilterValues}
            valuesState={searchFilterValuesStatus}
            onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
            onChangeStatus={(values) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...values }))}
            onSearch={() =>
              handleSearch({
                ...searchFilterValues,
                pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_RECEIVE_PAGE_CURSOR },
              })
            }
            onReset={handleResetFilter}
          />
          <Divider color="gray100" />
        </div>
      )}
      <ReceiveReturnDisposalTable
        data={listQuery.data?.list ?? null}
        rowSelection={rowSelection}
        pagination={{ ...searchFilterValues.pagination, totalCount: listQuery.data?.totalCount ?? 0 }}
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        isDownloadReady={isDownloadReady}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onClickViewDetail={handleClickViewDetail}
        onClickExcelDownload={handleClickExcelDownload}
        onChangeRowSelection={setRowSelection}
        onClickProcessRequest={handleClickProcessRequest}
      />
      {detailModal.isOpen && selectedInspectionId !== null && (
        <ReceiveReturnDisposalDetailModal
          isOpen={detailModal.isOpen}
          inspectionId={selectedInspectionId}
          onClose={detailModal.handleCloseModal}
        />
      )}
      {rtsDirectModal.isOpen && selectedInspectionIds.length > 0 && (
        <ReceiveReturnDisposalRtsDirectModal
          isOpen={rtsDirectModal.isOpen}
          inspectionIds={selectedInspectionIds}
          isRtsPendingInput={isRtsPendingInputSelection}
          onClose={rtsDirectModal.handleCloseModal}
          onSuccess={() => handleProcessSuccess(rtsDirectModal.handleCloseModal)}
        />
      )}
      {rtsParcelModal.isOpen && selectedInspectionIds.length > 0 && (
        <ReceiveReturnDisposalRtsParcelModal
          isOpen={rtsParcelModal.isOpen}
          inspectionIds={selectedInspectionIds}
          isRtsPendingInput={isRtsPendingInputSelection}
          onClose={rtsParcelModal.handleCloseModal}
          onSuccess={() => handleProcessSuccess(rtsParcelModal.handleCloseModal)}
        />
      )}
      {scrapModal.isOpen && selectedInspectionIds.length > 0 && (
        <ReceiveReturnDisposalScrapModal
          isOpen={scrapModal.isOpen}
          inspectionIds={selectedInspectionIds}
          onClose={scrapModal.handleCloseModal}
          onSuccess={() => handleProcessSuccess(scrapModal.handleCloseModal)}
        />
      )}
      {claimModal.isOpen && selectedInspectionIds.length > 0 && (
        <ReceiveReturnDisposalClaimModal
          isOpen={claimModal.isOpen}
          inspectionIds={selectedInspectionIds}
          onClose={claimModal.handleCloseModal}
          onSuccess={() => handleProcessSuccess(claimModal.handleCloseModal)}
        />
      )}
    </PageWrapper>
  );
};

export default ReceiveReturnDisposalPage;
